import areEqualValues from '@package/utils/src/areEqualValues';
import clsx from 'clsx';
import { useContext } from 'react';

import CircleIcon from '../Icon/CircleIcon';
import { RadioGroupContext } from '../RadioGroupContext';
import { RadioGroupContextValue } from '../RadioGroupContext/types';

import styles from './Radio.module.css';
import { RadioComponentProps, RadioGroupState } from './types';

export function useRadioGroup(): RadioGroupState | undefined {
  return useContext(RadioGroupContext);
}

const Radio = ({
  className,
  name: nameProp,
  value,
  id,
  checked: checkedProp,
  disabled,
  size = 'big',
  onChange: onChangeProp,
  radioLabelClassName,
  radioClassName,
  labelClassName,
  label,
  classNameLabel,
  customRadioContainerClasses,
  uncheckedClassName,
  textEndAdornment,
  textEndAdornmentClassName,
  endAdornment,
  endAdornmentClassName,
  canSelect = true,
  ...rest
}: RadioComponentProps) => {
  const radioContainerClasses: string = clsx(styles.radioContainer, className);
  const radioLabelClasses: string = clsx(styles.radioLabelRoot, radioLabelClassName, {
    [styles.disabledLabel]: disabled,
  });
  const radioClasses: string = clsx(styles.radio, 'radio', radioClassName);
  const labelClasses: string = clsx(styles.labelRoot, labelClassName);
  const unChekedClasses: string = clsx(
    styles.uncheked,
    'uncheked',
    uncheckedClassName,
    { [`checked ${styles.checked}`]: checkedProp },
    { [styles.unchekedSmall]: size === 'small' },
    { [styles.disabledUncheked]: disabled },
    { [styles.disabledChecked]: disabled && checkedProp },
  );
  const radioGroup: RadioGroupContextValue | undefined = useRadioGroup();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;

    if (canSelect) {
      if (onChangeProp) {
        onChangeProp(event);
      }
      if (radioGroup && radioGroup.onChange) {
        radioGroup.onChange(event);
      }
    }
  };

  let name = nameProp;
  let checked = checkedProp;
  if (radioGroup) {
    if (typeof checked === 'undefined') {
      checked = areEqualValues(radioGroup.value, value);
    }
    if (typeof name === 'undefined') {
      name = radioGroup.name;
    }
  }

  return (
    <div className={radioContainerClasses} data-testid={`${id}-container-${value}`} {...rest}>
      <label className={radioLabelClasses}>
        <span className={radioClasses}>
          <span className={styles.iconButtonLabel}>
            <input
              id={id}
              data-testid={`${id}-trigger-${value}`}
              type="radio"
              value={value}
              onChange={onChange}
              checked={checked}
              disabled={disabled}
              className={styles.radioInput}
              name={name}
            />
            <div className={unChekedClasses}>{checked && <CircleIcon />}</div>
          </span>
        </span>
        {label && (
          <span className={labelClasses} data-testid={`${id}-label-${value}`}>
            {typeof label === 'string' ? (
              <span className={clsx(styles.label, { [styles.disabledLabel]: disabled }, classNameLabel)}>{label}</span>
            ) : (
              <div
                className={clsx(styles.label, 'label', customRadioContainerClasses, {
                  [styles.disabledLabel]: disabled,
                })}
              >
                {label}
              </div>
            )}
          </span>
        )}
      </label>
      {textEndAdornment && <div data-testid={`${id}-text-end-adornment-${value}`} className={textEndAdornmentClassName}>{textEndAdornment}</div>}
      {endAdornment && <div data-testid={`${id}-end-adornment-${value}`} className={endAdornmentClassName}>{endAdornment}</div>}
    </div>
  );
};

export default Radio;
